import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { track } from '@amplitude/analytics-browser'
import { isCourseLimitReached } from 'src/hooks/useCourseCreation'
import { userLimitsGetAction } from 'src/reducers/user-limits/action'
import { selectUserLimits } from 'src/reducers/user-limits/selector'

/**
 * Shared "create course" flow: the plan's course limit check, the Create
 * from scratch modal and the AI Course Builder page.
 * Render `CreateCourseModal` with `scratchOpen` and `LimitReachedModal` with
 * `limitModalOpen`. `aiSearch` is appended to the AI Course Builder URL.
 */
const useCreateCourseFlow = ({ pathId, aiSearch = '' } = {}) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const userLimits = useSelector(selectUserLimits)
  const [scratchOpen, setScratchOpen] = useState(false)
  const [limitModalOpen, setLimitModalOpen] = useState(false)

  useEffect(() => {
    if (!userLimits) dispatch(userLimitsGetAction())
  }, [dispatch, userLimits])

  const select = useCallback(
    option => {
      if (isCourseLimitReached(userLimits)) {
        setLimitModalOpen(true)
        return
      }
      if (option === 'scratch') {
        track('from_scratch')
        setScratchOpen(true)
        return
      }
      track('ai-course-creation')
      history.push(
        `/ai-course-creation?collectionId=${pathId || 'undefined'}${aiSearch}`,
      )
    },
    [aiSearch, history, pathId, userLimits],
  )

  return {
    select,
    scratchOpen,
    closeScratch: useCallback(() => setScratchOpen(false), []),
    limitModalOpen,
    closeLimitModal: useCallback(() => setLimitModalOpen(false), []),
  }
}

export default useCreateCourseFlow
